import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { SimulationNotification } from '../../../core/models/SimulationNotification';
import { NotifPollingService } from '../../../core/services/notif-polling.service';
import { Subject } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';
import  {isEqual} from 'lodash-es';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SimulationSummaryModalComponent } from '../simulation-summary-modal/simulation-summary-modal.component';
import { Router } from '@angular/router';
import { Store } from '../../../store';
import { takeUntil } from 'rxjs/operators';

/**
 * Popup for 'new' Simulation notifications
 *
 * The UI differs if there is only one new notification, or several.
 *
 * @export
 * @class NotificationAlertComponent
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-notification-alert',
  templateUrl: './notification-alert.component.html',
  styleUrls: ['./notification-alert.component.scss'],
  standalone: false
})
export class NotificationAlertComponent implements OnInit, OnDestroy {

  /**
   * These are the 'new' Simulation notifications to report.
   *
   * @type {SimulationNotification[]}
   * @memberof NotificationAlertComponent
   */
  newNotifications: SimulationNotification[] = [];

  /**
     * Unsubscribe all the observables of the component on exit using a generic trigger.
   *
   * @private
   * @type {Subject<void>}
   * @memberof NotificationAlertComponent
   */
  public unsubscribe: Subject<void> = new Subject();

  /**
   * Creates an instance of NotificationAlertComponent.
   *
   * @param {NotifPollingService} notifService
   * @param {NgbModal} modalService
   * @param {Router} router
   * @param {Store} store
   * @memberof NotificationAlertComponent
   */
  constructor(private notifService: NotifPollingService,
              private modalService: NgbModal,
              private router: Router,
              private store: Store) { }

  /**
   * Initialize the component: fetch the notifications with the 'isnew' flag.
   *
   * @memberof NotificationAlertComponent
   */
  ngOnInit() {
    this.notifService.publishedNotifs$.pipe(
      map((items: SimulationNotification[]) => items.filter((item: SimulationNotification) => item.isNew)),
      distinctUntilChanged((prev: SimulationNotification[], curr: SimulationNotification[]) => isEqual(prev, curr))
    ).pipe(takeUntil(this.unsubscribe))
      .subscribe((items: SimulationNotification[]) => this.newNotifications = items);
  }

  /**
   * Unsubscribe to all observablers using a generic trigger.
   *
   * @memberof NotificationAlertComponent
   */
  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  /**
   * Once the popup has shown a notification, it is no longer 'new'.
   *
   * --> update the notification cache.
   * --> update the notification in the backend.
   *
   * @memberof NotificationAlertComponent
   */
  public unsetNew() {
    const list = [];
    for (const notif of this.newNotifications) {
      notif.isNew = false;
      list.push(notif);
    }
    if (list.length > 0) {
      this.notifService.updateExistingNotifications(list);
    }
    this.newNotifications = [];
    this.notifService.broadcastNotificationList();
  }

  /**
   * open a modal with the details of the simulations.
   *
   * @memberof NotificationAlertComponent
   */
  public showDetails(item: SimulationNotification) {
    const modalRef = this.modalService.open(SimulationSummaryModalComponent, { centered: true, windowClass: 'model-instruments-modal' });
    modalRef.result.then().catch(err => {});
    modalRef.componentInstance.modelID = item.modelID;
    modalRef.componentInstance.entityName = item.entity;
  }

  /**
   * Once the user has clicked on the close glyph of a notification, it will no longer be displayed anywhere.
   *
   * @memberof NotificationAlertComponent
   */
  unsetVisible() {
    for (const notif of this.newNotifications) {
      notif.isVisible = false;
      this.notifService.updateExistingNotifications([notif]);
    }
    this.newNotifications = [];
    this.notifService.broadcastNotificationList();
  }

  /**
   * The 'show notifications' button forces the notification panel to visible.
   *
   * @memberof NotificationAlertComponent
   */
  showPanel() {
    this.notifService.showNotifPanel();
  }

  /**
   * Show simulation results 
   *
   * @param {SimulationNotification} item 
   * @memberof NotificationAlertComponent
   */
  doView(item: SimulationNotification) {
      if (item.status === 'Completed') {
          const url = `/entitySummary/${item.entityID}`;
          this.store.value.urlModelToggle[url] = true;
          this.store.value.modellingConfig.modelId = item.modelID;
          this.store.set('urlModelToggle', this.store.value.urlModelToggle);
          this.store.set('modellingConfig', this.store.value.modellingConfig);
          this.router.navigateByUrl(url).then();
      } else {
          this.showDetails(item);
    }
    this.unsetNew();
  }

}
